import { dateCompare1, isNullOrWhitespace, strCompareLowerCase } from "@common/Common";
import PortfolioOrDecisionNavigation from '@common/Components/PortfolioOrDecisionNavigation.vue';
import { dynamicUiRoutes } from "@common/dynamicUiRoutes";
import { guid } from '@common/Guid';
import { ICommandStatus } from '@cqrs/cqrs';
import { ICommandBase } from '@Reports/cqrs';
import { ReportBuilderViewModel } from "@Reports/Reports/Components/ReportBuilder";
import ReportBuilder from '@Reports/Reports/Components/ReportBuilder.vue';
import { ReportEditorData, ReportEditorViewModel } from '@Reports/Reports/Components/ReportEditor';
import ReportEditor from '@Reports/Reports/Components/ReportEditor.vue';
import { CloneReportCommandV1, CreateReportCommandV1, DeleteReportCommandV1, UpdateReportCommandV1 } from '@Reports/Reports/Domain/commands-core';
import ReportsCommands from "@Reports/Reports/Domain/commands-vue-component";
import ReportsQueries from "@Reports/Reports/Domain/queries-vue-component";
import { ReportReadModel, ReportsPageReadModel } from "@Reports/Reports/Domain/readmodels";
import moment from "moment";
import { Component, mixins } from 'vue-facing-decorator';

@Component({
    components: { PortfolioOrDecisionNavigation, ReportBuilder, ReportEditor }
})
export default class ReportsPage extends mixins(ReportsCommands, ReportsQueries)
{
    get workspaceId(): number { return Number.parseInt(this.$route.params.workspaceId) }
    get projectId(): number { return Number.parseInt(this.$route.params.projectId) }

    url_ReportViewPage(reportId: string): string
    {
        return dynamicUiRoutes.Reports_ReportViewPage(this.$route, this.workspaceId, this.projectId, reportId);
    }


    rm: ReportsPageReadModel | null = null;

    SearchPhrase: string = null;
    OrderBy: string = "created";
    OrderByDescending: boolean = true;
    creating: boolean = false;

    reportBuilder: ReportBuilderViewModel = new ReportBuilderViewModel();
    reportEditor: ReportEditorViewModel = new ReportEditorViewModel();

    get ReportsView(): ReportReadModel[]
    {
        if (this.rm == null)
        {
            return null;
        }

        let view = new Array(...this.rm.Reports);

        if (!isNullOrWhitespace(this.SearchPhrase))
        {
            view = this.rm.Reports.filter(o => o.Name.toLowerCase().indexOf(this.SearchPhrase.toLowerCase()) >= 0);
        }

        if (this.OrderBy === "name")
        {
            view = view.sort((a, b) => strCompareLowerCase(a.Name, b.Name));
        }
        else if (this.OrderBy === "modified")
        {
            view = view.sort((a, b) => dateCompare1(a.Modified, b.Modified));
        }
        else if (this.OrderBy === "created")
        {
            view = view.sort((a, b) => dateCompare1(a.Created, b.Created));
        }

        if (this.OrderByDescending === true)
        {
            view = view.reverse();
        }

        return view;
    }


    getFormattedDateStr(date: string): string 
    {
        if (date)
        {
            return moment(date).format('MMM D, YYYY');
        }

        return null;
    }

    getFormattedTimeStr(date: string): string 
    {
        if (date)
        {
            return moment(date).format('LT');
        }

        return null;
    }

    showReportCreator()
    {
        this.reportBuilder.show();
    }

    showReportEditor(vm: ReportReadModel)
    {
        this.reportEditor.show({ Id: vm.ReportId, Name: vm.Name, Description: vm.Description });
    }


    OrderByNameCommand()
    {
        if (this.OrderBy === 'name')
        {
            this.OrderByDescending = !this.OrderByDescending;
        }
        else
        {
            this.OrderByDescending = false;
            this.OrderBy = 'name';
        }
    }

    OrderByCreatedCommand()
    {
        if (this.OrderBy === 'created')
        {
            this.OrderByDescending = !this.OrderByDescending;
        }
        else
        {
            this.OrderByDescending = true;
            this.OrderBy = 'created';
        }
    }

    OrderByModifiedCommand()
    {
        if (this.OrderBy === 'modified')
        {
            this.OrderByDescending = !this.OrderByDescending;
        }
        else
        {
            this.OrderByDescending = true;
            this.OrderBy = 'modified';
        }
    }


    handleCreateReportCommand(reportName: string)
    {
        this.invokeCreateReportCommandV1(this.workspaceId, this.projectId, guid(), reportName, null);
    }

    handleUpdateReportCommand(data: ReportEditorData)
    {
        this.invokeUpdateReportCommandV1(this.workspaceId, data.Id, data.Name, data.Description);
    }

    handleDeleteReportCommand(r: ReportReadModel)
    {
        if (confirm("Are you sure?") === true)
        {
            this.invokeDeleteReportCommandV1(this.workspaceId, r.ReportId);
        }
    }

    handleCloneReportCommand(r: ReportReadModel)
    {
        this.invokeCloneReportCommandV1(this.workspaceId, guid(), r.ReportId);
    }


    mounted()
    {
        document.title = "Reports | TransparentChoice";
        this.runReportsPageQueryV1(this.workspaceId, this.projectId);
    }

    onSetModel(model: any, modelName: string) 
    {
        if (modelName == "ReportsPageReadModel")
        {
            this.rm = model as ReportsPageReadModel;
        }
        else if (modelName == "ReportReadModel")
        {
            const rm = model as ReportReadModel;

            const curr = this.rm.Reports.find(a => a.ReportId === rm.ReportId);

            if (curr)
            {
                this.rm.Reports.remove(curr);
            }

            this.rm.Reports.push(rm);
        }
    }

    onCommandSucceed(cmd: ICommandBase, correlationId: string, result: any)
    {
        if (CreateReportCommandV1.matchType(cmd))
        {
            this.reportBuilder.close();
            location.href = this.url_ReportViewPage(correlationId);
        }
        else if (DeleteReportCommandV1.matchType(cmd))
        {
            const reportId = correlationId;
            this.rm.Reports = this.rm.Reports.filter(r => r.ReportId !== reportId);
        }
        else if (CloneReportCommandV1.matchType(cmd))
        {
            const reportId = correlationId;
            this.runReportQueryV1(this.workspaceId, reportId);
        }
        else if (UpdateReportCommandV1.matchType(cmd))
        {
            const reportId = correlationId;
            this.runReportQueryV1(this.workspaceId, reportId);
            this.reportEditor.close();
        }
    }

    onCommandFailed(status: ICommandStatus)
    {
        if (CreateReportCommandV1.matchType(status.Command))
        {
            for (const error of status.ValidationErrors)
            {
                this.reportBuilder.reportNameError = error.Error;
            }
        }
        else if (UpdateReportCommandV1.matchType(status.Command))
        {
            this.reportEditor.displayValidationErrors(status.ValidationErrors);
        }
    }
}